import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// UK-WHO centile reference data (simplified for visualization)
const CENTILE_LINES = [3, 15, 50, 85, 97];

const RCPCHGrowthChart = ({ measurements, childData, chartType = 'height' }) => {
  if (!measurements || measurements.length === 0) {
    return (
      <div className="bg-[#02b5ff]/10 border border-[#02b5ff]/30 rounded-xl p-6 text-center">
        <p className="text-[#02b5ff] font-medium">No measurement data available</p>
        <p className="text-[#02b5ff]/70 text-sm mt-1">Add measurements to view growth charts</p>
      </div>
    );
  }

  // Prepare child's measurement data
  const childData_points = measurements.map(m => ({
    age: m.age_years,
    value: chartType === 'height' ? m.standing_height : 
           chartType === 'weight' ? m.weight : m.bmi,
    percentile: chartType === 'height' ? m.uk_who_percentiles?.height_percentile :
                chartType === 'weight' ? m.uk_who_percentiles?.weight_percentile :
                m.bmi_percentile,
    date: new Date(m.measurement_date).toLocaleDateString()
  }));

  // Get age range for x-axis
  const minAge = Math.floor(Math.min(...childData_points.map(p => p.age)));
  const maxAge = Math.ceil(Math.max(...childData_points.map(p => p.age))) + 1;
  const ageLabels = [];
  for (let age = minAge; age <= maxAge; age += 0.5) {
    ageLabels.push(age.toFixed(1));
  }

  // Chart configuration
  const chartConfig = {
    height: {
      label: 'Height (cm)',
      color: '#3B82F6',
      bgColor: 'rgba(59, 130, 246, 0.1)',
      yMin: 80,
      yMax: 200,
      title: 'Height-for-Age Chart'
    },
    weight: {
      label: 'Weight (kg)',
      color: '#10B981',
      bgColor: 'rgba(16, 185, 129, 0.1)',
      yMin: 10,
      yMax: 100,
      title: 'Weight-for-Age Chart'
    },
    bmi: {
      label: 'BMI (kg/m²)',
      color: '#8B5CF6',
      bgColor: 'rgba(139, 92, 246, 0.1)',
      yMin: 12,
      yMax: 30,
      title: 'BMI-for-Age Chart'
    }
  };

  const config = chartConfig[chartType];

  // Adjust y-axis based on actual data
  const values = childData_points.map(p => p.value);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const yMin = Math.floor(dataMin * 0.9);
  const yMax = Math.ceil(dataMax * 1.1);

  const data = {
    labels: childData_points.map(p => p.age.toFixed(1)),
    datasets: [
      {
        label: `${childData.first_name}'s ${config.label}`,
        data: childData_points.map(p => p.value),
        borderColor: config.color,
        backgroundColor: config.bgColor,
        borderWidth: 3,
        pointRadius: 8,
        pointBackgroundColor: config.color,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverRadius: 10,
        fill: false,
        tension: 0.3
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: { size: 12, weight: 'bold' },
          padding: 20
        }
      },
      title: {
        display: true,
        text: config.title,
        font: { size: 18, weight: 'bold' },
        padding: { top: 10, bottom: 20 }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          title: (items) => {
            const idx = items[0].dataIndex;
            return `Age: ${childData_points[idx].age.toFixed(1)} years`;
          },
          label: (item) => {
            const idx = item.dataIndex;
            const point = childData_points[idx];
            const lines = [
              `${chartType.charAt(0).toUpperCase() + chartType.slice(1)}: ${point.value}`,
              `Percentile: ${point.percentile?.toFixed(1) || 'N/A'}%`,
              `Date: ${point.date}`
            ];
            return lines;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Age (years)',
          font: { size: 14, weight: 'bold' },
          padding: { top: 10 }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: { size: 11 }
        }
      },
      y: {
        title: {
          display: true,
          text: config.label,
          font: { size: 14, weight: 'bold' },
          padding: { bottom: 10 }
        },
        min: yMin,
        max: yMax,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: { size: 11 }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  // Calculate current percentile info
  const latestMeasurement = childData_points[childData_points.length - 1];
  const percentile = latestMeasurement?.percentile;

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Chart Header with Percentile Info */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{config.title}</h3>
            <p className="text-sm text-gray-500">UK-WHO Growth Reference</p>
          </div>
          {percentile !== undefined && percentile !== null && (
            <div className="text-right">
              <div className="text-sm text-gray-500">Latest Percentile</div>
              <div className={`text-2xl font-bold ${
                percentile < 3 || percentile > 97 ? 'text-[#ff025f]' :
                percentile < 15 || percentile > 85 ? 'text-[#02b5ff]' :
                'text-[#02b5ff]'
              }`}>
                {percentile.toFixed(0)}th
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="p-6" style={{ height: '400px' }}>
        <Line data={data} options={options} />
      </div>

      {/* Percentile Guide */}
      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Percentile Guide</h4>
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#02b5ff]"></div>
            <span className="text-gray-600">15th-85th: Typical range</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#02b5ff]"></div>
            <span className="text-gray-600">3rd-15th or 85th-97th: Monitor</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#ff025f]"></div>
            <span className="text-gray-600">&lt;3rd or &gt;97th: Review recommended</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RCPCHGrowthChart;
